import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ShieldOff, KeyRound, RefreshCw, Check, Copy, AlertTriangle, Trash2, Dices,
} from 'lucide-react';
import { instances as instancesApi, type BryveErgebnis } from '../api';

/**
 * Notfallzugriff auf eine bryve-Instanz: Sperren aufheben, Kennwort setzen.
 *
 * ══ WARUM DAS HIER STEHT UND NICHT IM PRODUKT ══════════════════════════════
 *
 * bryve hat beides schon als Schnittstelle (`/api/admin/ip-unblock`,
 * `/api/admin/reset-passwords`), beide mit dem Kopf `X-Admin-Key` statt einer
 * Anmeldung — ausdruecklich fuer den Fall, dass niemand mehr hineinkommt.
 *
 * Nur ist die Sperrpruefung die **erste** Middleware und laesst genau zwei
 * Pfade durch: `/health` und `/api/csp-report`. Wer gesperrt ist, bekommt auch
 * auf dem Weg, der ihn entsperren soll, ein 429. Von aussen ist der Notausgang
 * also verschlossen.
 *
 * Der PC hat SSH auf den Wirt und ruft die Schnittstelle **im Container** ueber
 * 127.0.0.1 auf. Damit faellt alles weg, was dazwischen klemmen kann: die
 * Sperre, der Proxy, Cloudflare, die Zugangsliste. Antwortet die Anwendung gar
 * nicht, geht es ueber die Datenbank weiter — das Ergebnis sagt, welcher Weg
 * getragen hat.
 */

/** Ein Kennwort, das man vorlesen kann, ohne es zu buchstabieren. */
function kennwortVorschlag(): string {
  const zeichen = 'abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const werte = new Uint32Array(20);
  crypto.getRandomValues(werte);
  return Array.from(werte, (v) => zeichen[v % zeichen.length]).join('');
}

function WegListe({ wege }: { wege: BryveErgebnis['wege'] }) {
  if (!wege.length) return null;
  return (
    <ul className="mt-1 space-y-0.5">
      {wege.map((w, i) => (
        <li key={i} className="text-[10px] text-slate-500">
          {w.ok ? '✓' : '✗'} {w.name === 'schnittstelle' ? 'Schnittstelle' : 'Datenbank'}
          {w.meldung ? ` — ${w.meldung}` : ''}
        </li>
      ))}
    </ul>
  );
}

export default function BryveNotfallPanel({ instanceId }: { instanceId: string }) {
  const qc = useQueryClient();
  const [gewaehlt, setGewaehlt] = useState('');
  const [kennwort, setKennwort] = useState('');
  const [kopiert, setKopiert] = useState(false);
  const [ergebnis, setErgebnis] = useState<BryveErgebnis | null>(null);

  const konten = useQuery({
    queryKey: ['bryve-superadmins', instanceId],
    queryFn: () => instancesApi.bryveSuperadmins(instanceId),
    retry: false,
  });

  const sperren = useQuery({
    queryKey: ['bryve-sperren', instanceId],
    queryFn: () => instancesApi.bryveSperren(instanceId),
    retry: false,
  });

  const setzen = useMutation({
    mutationFn: () => instancesApi.bryvePasswortSetzen(instanceId, gewaehlt, kennwort),
    onSuccess: (e) => {
      setErgebnis(e);
      if (e.ok) qc.invalidateQueries({ queryKey: ['instances', instanceId] });
    },
    onError: (e: Error) => setErgebnis({ ok: false, meldung: e.message, wege: [] }),
  });

  const aufheben = useMutation({
    mutationFn: (ip?: string) => instancesApi.bryveSperrenAufheben(instanceId, ip),
    onSuccess: (e) => {
      setErgebnis(e);
      qc.invalidateQueries({ queryKey: ['bryve-sperren', instanceId] });
    },
    onError: (e: Error) => setErgebnis({ ok: false, meldung: e.message, wege: [] }),
  });

  const liste = sperren.data?.sperren ?? [];

  return (
    <div className="space-y-3">
      {/* ── Kennwort ──────────────────────────────────────────────────── */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-4 py-2.5">
          <h3 className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
            <KeyRound size={14} /> Kennwort eines Admin-Kontos setzen
          </h3>
          <p className="mt-0.5 text-[11px] leading-relaxed text-slate-500">
            Über den Wirt, nicht von aussen — funktioniert auch, wenn die Adresse gesperrt ist,
            der Proxy klemmt oder niemand mehr hineinkommt.
          </p>
          {/* Der Gegenverweis zur Karte „Betreiberkonto“. Beide Stellen sagen
              „Passwort", und ohne diesen Satz probiert man die falsche zuerst. */}
          <p className="mt-1.5 rounded bg-sky-50 px-2 py-1.5 text-[11px] leading-relaxed text-sky-900">
            <strong>Nicht zu verwechseln</strong> mit „Neues Passwort erzeugen" oben: das
            schreibt in die Konfiguration im PC und wirkt erst beim nächsten Ausrollen — und
            nur, wenn das Konto dann noch nicht existiert. Hier wird es dort gesetzt, wo das
            Konto liegt: sofort gültig, für jedes Admin-Konto der Instanz.
          </p>
        </div>

        <div className="space-y-2 px-4 py-3">
          <label className="block">
            <span className="mb-0.5 block text-[10px] text-slate-500">
              Konto {konten.data ? `(${konten.data.konten.length} gefunden)` : ''}
            </span>
            <select
              value={gewaehlt}
              onChange={(e) => setGewaehlt(e.target.value)}
              disabled={konten.isLoading}
              className="w-full rounded border border-slate-300 px-2 py-1.5 text-xs disabled:bg-slate-50"
            >
              <option value="">
                {konten.isLoading ? 'wird aus der Instanz gelesen …' : '— wählen —'}
              </option>
              {konten.data?.konten.map((k) => (
                <option key={k.email} value={k.email}>
                  {k.email}{k.name ? ` — ${k.name}` : ''} ({k.rolle})
                </option>
              ))}
            </select>
          </label>

          {konten.isError && (
            <p className="text-[11px] text-rose-700">
              Die Kontenliste liess sich nicht lesen: {(konten.error as Error).message}
            </p>
          )}

          <label className="block">
            <span className="mb-0.5 block text-[10px] text-slate-500">
              Neues Kennwort (mindestens acht Zeichen)
            </span>
            <div className="flex gap-1.5">
              <input
                type="text"
                value={kennwort}
                onChange={(e) => setKennwort(e.target.value)}
                placeholder="…"
                className="min-w-0 flex-1 rounded border border-slate-300 px-2 py-1.5 font-mono text-xs"
              />
              <button
                type="button"
                title="Vorschlag erzeugen"
                onClick={() => setKennwort(kennwortVorschlag())}
                className="rounded border border-slate-300 px-2 py-1.5 text-slate-600 hover:bg-slate-50"
              >
                <Dices size={13} />
              </button>
              {kennwort && (
                <button
                  type="button"
                  title="In die Zwischenablage"
                  onClick={() => {
                    navigator.clipboard.writeText(kennwort);
                    setKopiert(true);
                    window.setTimeout(() => setKopiert(false), 2000);
                  }}
                  className="rounded border border-slate-300 px-2 py-1.5 text-slate-600 hover:bg-slate-50"
                >
                  {kopiert ? <Check size={13} /> : <Copy size={13} />}
                </button>
              )}
            </div>
          </label>

          {/* Im Klartext und nicht als Passwortfeld: wer es gerade setzt, muss
              es weitergeben können. Verborgen wäre es nur scheinbar sicherer —
              man tippt es dann zweimal und verschreibt sich einmal. */}
          <p className="text-[10px] leading-relaxed text-slate-500">
            Ist das gewählte Konto das Betreiberkonto der Instanz, legt der PC den neuen Wert
            zugleich als Geheimnis ab — dann stimmen beide Seiten wieder überein.
          </p>

          <button
            type="button"
            onClick={() => setzen.mutate()}
            disabled={!gewaehlt || kennwort.length < 8 || setzen.isPending}
            className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-40"
          >
            {setzen.isPending ? 'setzt …' : 'Kennwort setzen'}
          </button>
        </div>
      </div>

      {/* ── Sperren ───────────────────────────────────────────────────── */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="flex flex-wrap items-start justify-between gap-2 border-b border-slate-200 px-4 py-2.5">
          <div>
            <h3 className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
              <ShieldOff size={14} /> Gesperrte Adressen
            </h3>
            <p className="mt-0.5 text-[11px] leading-relaxed text-slate-500">
              bryve sperrt nach fünf Fehlversuchen für eine Viertelstunde, nach
              2FA-Fehlversuchen für eine Stunde.
            </p>
          </div>
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => sperren.refetch()}
              disabled={sperren.isFetching}
              className="rounded-lg border border-slate-300 px-2 py-1.5 text-slate-600 hover:bg-slate-50 disabled:opacity-40"
            >
              <RefreshCw size={12} className={sperren.isFetching ? 'animate-spin' : ''} />
            </button>
            <button
              type="button"
              onClick={() => aufheben.mutate(undefined)}
              disabled={aufheben.isPending || liste.length === 0}
              className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-40"
            >
              {aufheben.isPending ? 'hebt auf …' : 'Alle aufheben'}
            </button>
          </div>
        </div>

        {sperren.isError ? (
          <p className="px-4 py-3 text-[11px] text-rose-700">
            {(sperren.error as Error).message}
          </p>
        ) : sperren.isLoading ? (
          <p className="px-4 py-3 text-[11px] text-slate-400">wird gelesen …</p>
        ) : liste.length === 0 ? (
          <p className="px-4 py-3 text-[11px] text-slate-500">Keine Adresse ist gesperrt.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {liste.map((s) => (
              <li key={s.ip} className="flex items-start gap-2 px-4 py-2">
                <span className="min-w-0 flex-1">
                  <code className="font-mono text-xs text-slate-800">{s.ip}</code>
                  <span className="ml-2 text-[11px] text-slate-500">{s.grund}</span>
                  <span className="block text-[10px] text-slate-400">
                    frei ab {new Date(s.laeuftAbAm).toLocaleString('de-DE')}
                    {s.treffer > 0 && ` · ${s.treffer} Versuche seither`}
                  </span>
                </span>
                <button
                  type="button"
                  title="Diese Adresse freigeben"
                  onClick={() => aufheben.mutate(s.ip)}
                  disabled={aufheben.isPending}
                  className="shrink-0 rounded border border-slate-300 px-2 py-1 text-[10px] text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                >
                  <Trash2 size={10} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ── Rückmeldung ───────────────────────────────────────────────── */}
      {ergebnis && (
        <div
          className={`rounded-lg border px-3 py-2 ${
            ergebnis.ok
              ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
              : 'border-amber-200 bg-amber-50 text-amber-900'
          }`}
        >
          <div className="flex items-start gap-1.5">
            {ergebnis.ok ? <Check size={13} className="mt-0.5 shrink-0" />
              : <AlertTriangle size={13} className="mt-0.5 shrink-0" />}
            <div className="min-w-0 flex-1">
              <p className="text-xs leading-relaxed">{ergebnis.meldung}</p>
              <WegListe wege={ergebnis.wege} />
            </div>
            <button
              type="button"
              onClick={() => setErgebnis(null)}
              className="shrink-0 text-xs opacity-60 hover:opacity-100"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
